// Apolo IDE — Cmd+K inline edit popup (Sprint v11 Epic E1).
//
// Floats over the editor at the selection's screen coordinates. User types
// an instruction ("refactor to async/await"), submits, and we stream the
// replacement code from /api/code-ide/inline-edit. Cmd+Enter accepts the
// streamed result; Esc rejects. Accept dispatches a CodeMirror change that
// replaces the original selection range.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, Check, X, Loader2 } from 'lucide-react';
import { streamSSE } from '../../lib/sseClient.js';

const PHASE_IDLE = 'idle';
const PHASE_STREAMING = 'streaming';
const PHASE_READY = 'ready';
const PHASE_ERROR = 'error';

/**
 * @param {{
 *   anchor: { x: number, y: number },              // popup top-left coords
 *   selectionText: string,
 *   filePath: string,
 *   language?: string,
 *   sessionId?: string,
 *   modelId?: string,
 *   contextBefore?: string,
 *   contextAfter?: string,
 *   onAccept: (newText: string) => void,
 *   onReject: () => void,
 * }} props
 */
export default function InlineEditPopup({
  anchor,
  selectionText,
  filePath,
  language,
  sessionId,
  modelId,
  contextBefore,
  contextAfter,
  onAccept,
  onReject,
}) {
  const [instruction, setInstruction] = useState('');
  const [output, setOutput] = useState('');
  const [phase, setPhase] = useState(PHASE_IDLE);
  const [errorMsg, setErrorMsg] = useState(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Auto-focus input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!instruction.trim() || phase === PHASE_STREAMING) return;

      setOutput('');
      setErrorMsg(null);
      setPhase(PHASE_STREAMING);

      const controller = new AbortController();
      abortRef.current = controller;

      const result = await streamSSE('/api/code-ide/inline-edit', {
        body: {
          session_id: sessionId,
          file_path: filePath,
          language,
          selection_text: selectionText,
          context_before: contextBefore,
          context_after: contextAfter,
          instruction: instruction.trim(),
          model_id: modelId,
        },
        signal: controller.signal,
        onChunk: (chunk) => {
          if (chunk?.type === 'text' && typeof chunk.delta === 'string') {
            setOutput((prev) => prev + chunk.delta);
          }
        },
      });

      if (controller.signal.aborted) {
        setPhase(PHASE_IDLE);
        return;
      }
      if (!result.ok) {
        setErrorMsg(result.error || 'Falha no stream');
        setPhase(PHASE_ERROR);
        return;
      }
      setPhase(PHASE_READY);
    },
    [
      instruction,
      phase,
      sessionId,
      filePath,
      language,
      selectionText,
      contextBefore,
      contextAfter,
      modelId,
    ],
  );

  const handleAccept = useCallback(() => {
    if (!output) return;
    onAccept?.(output);
  }, [output, onAccept]);

  const handleReject = useCallback(() => {
    abortRef.current?.abort();
    onReject?.();
  }, [onReject]);

  // Global key handler — Esc cancels everywhere, Cmd+Enter accepts when ready.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleReject();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (phase === PHASE_READY) handleAccept();
        else if (phase === PHASE_IDLE) handleSubmit();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase, handleAccept, handleReject, handleSubmit]);

  // Cleanup any pending stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Position the popup — anchor is the selection's bottom-left in viewport
  // coords. Clamp to keep it on screen (44px padding margin minimum).
  const left = Math.min(Math.max(anchor.x, 8), window.innerWidth - 460);
  const top = Math.min(anchor.y + 8, window.innerHeight - 280);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Editar seleção com IA"
      className="fixed z-50 w-[450px] rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 shadow-xl"
      style={{ left, top }}
    >
      <form onSubmit={handleSubmit} className="border-b border-apolo-borderSubtle p-3">
        <label htmlFor="apolo-inline-edit-input" className="sr-only">
          Instrução de edição
        </label>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            id="apolo-inline-edit-input"
            type="text"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="ex: refatora pra usar async/await"
            disabled={phase === PHASE_STREAMING}
            className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-3 py-2 text-sm text-apolo-textPrimary outline-none focus:border-apolo-accent disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!instruction.trim() || phase === PHASE_STREAMING}
            aria-label="Executar edição"
            className="flex h-9 w-9 items-center justify-center rounded-md bg-apolo-accent text-white transition-colors hover:bg-apolo-accentHover disabled:opacity-40"
          >
            {phase === PHASE_STREAMING ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
        <p className="mt-2 text-xs text-apolo-textTertiary">
          {phase === PHASE_IDLE && 'Enter pra enviar · Esc pra cancelar'}
          {phase === PHASE_STREAMING && 'Gerando edição…'}
          {phase === PHASE_READY && 'Cmd+Enter pra aceitar · Esc pra rejeitar'}
          {phase === PHASE_ERROR && <span className="text-apolo-error">{errorMsg}</span>}
        </p>
      </form>

      {output && (
        <div className="max-h-[200px] overflow-auto p-3">
          <pre className="whitespace-pre-wrap break-words rounded bg-apolo-bg p-2 text-xs text-apolo-textPrimary">
            <code>{output}</code>
          </pre>
        </div>
      )}

      {phase === PHASE_READY && (
        <div className="flex items-center justify-end gap-2 border-t border-apolo-borderSubtle p-2">
          <button
            type="button"
            onClick={handleReject}
            className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-apolo-textTertiary transition-colors hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Rejeitar
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="flex items-center gap-1 rounded-md bg-apolo-success px-3 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90"
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Aceitar
          </button>
        </div>
      )}
    </div>
  );
}
